import { useMsal } from '@azure/msal-react';
import { FC, ReactNode, useEffect, useRef } from 'react';
import { loginRequest } from '../../auth/authConfig';
import { useAuth } from '../../auth/useAuth';
import { logger } from '../../utils/logger';

interface IProtectedRouteProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export const ProtectedRoute: FC<IProtectedRouteProps> = ({
  children,
  fallback,
}) => {
  const { isAuthenticated, isLoading, user, account } = useAuth();
  const { instance } = useMsal();
  const isTestApiKeyMode = Boolean(import.meta.env.VITE_TEST_API_KEY);
  const hasTriggeredRedirect = useRef(false);

  // Debug logging
  useEffect(() => {
    logger.debug('🛡️ ProtectedRoute Debug:', {
      isAuthenticated,
      isLoading,
      hasUser: !!user,
      hasAccount: !!account,
      hasTriggeredRedirect: hasTriggeredRedirect.current,
    });
  }, [isAuthenticated, isLoading, user, account]);

  // Automatically redirect to login if not authenticated
  useEffect(() => {
    if (isTestApiKeyMode) {
      return;
    }

    // Guard: also check the MSAL instance directly.
    // With MSAL React v3 + MSAL Browser v4, the React context state (accounts)
    // can briefly lag behind the underlying MSAL cache — e.g. inProgress updates
    // to None in one dispatch before accounts are populated in a separate dispatch.
    // During that window, isAuthenticated would be false even though a valid
    // account exists in the cache.  Checking getAllAccounts() directly prevents
    // a spurious loginRedirect in that transient state, which would otherwise
    // create a redirect loop.
    const hasAccountInCache = instance.getAllAccounts().length > 0;

    if (
      !isLoading &&
      !isAuthenticated &&
      !hasTriggeredRedirect.current &&
      !hasAccountInCache
    ) {
      logger.debug(
        '🔄 ProtectedRoute: Not authenticated, initiating automatic login redirect'
      );
      hasTriggeredRedirect.current = true;

      // navigateToLoginRequestUrl: true in authConfig means MSAL will
      // automatically navigate back to the current page after login, so we
      // do not need to store a custom redirect path here.

      // Trigger automatic login redirect
      instance.loginRedirect(loginRequest).catch((error) => {
        logger.error('❌ Automatic login redirect failed:', error);
        hasTriggeredRedirect.current = false; // Reset on error
      });
    }
  }, [isLoading, isAuthenticated, instance, isTestApiKeyMode]);

  if (isTestApiKeyMode) {
    return <>{children}</>;
  }

  if (isLoading) {
    logger.debug('🔄 ProtectedRoute: Loading...');
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100dvh',
          flexDirection: 'column',
          fontFamily: 'Arial, sans-serif',
        }}
      >
        <div
          style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        ></div>
        <span style={{ marginTop: '15px', fontSize: '16px' }}>
          Loading authentication...
        </span>
      </div>
    );
  }

  if (!isAuthenticated) {
    logger.debug(
      '🚫 ProtectedRoute: Not authenticated, redirecting to login...'
    );

    // Show fallback if provided, otherwise show loading state while redirect happens
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100dvh',
          backgroundColor: '#f8f9fa',
          fontFamily: 'Arial, sans-serif',
        }}
      >
        <div
          style={{
            textAlign: 'center',
            padding: '40px',
            backgroundColor: 'white',
            borderRadius: '10px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            maxWidth: '400px',
          }}
        >
          <h1
            style={{
              fontSize: '2rem',
              fontWeight: 'bold',
              marginBottom: '16px',
              color: '#333',
            }}
          >
            🚀 TF-PULSE
          </h1>
          <p
            style={{
              marginBottom: '24px',
              color: '#666',
              fontSize: '16px',
            }}
          >
            Redirecting to Microsoft authentication...
          </p>
          <div
            style={{
              width: '40px',
              height: '40px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #0078d4',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto',
            }}
          ></div>
        </div>
      </div>
    );
  }

  logger.debug('✅ ProtectedRoute: Authenticated, showing children');
  return <>{children}</>;
};
